import { ColorSwatch, Flex, Paper, Text, Title } from "@mantine/core";
import {
  Line,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useMediaQuery } from "@mantine/hooks";
import { LikeResponse } from "../../../entities/components/LikeResponse";
import useReportStore from "../../../store/useReportStore";

const COLORS = [
  "#05D8D2",
  "#A64DD7",
  "#6F40E2",
  "#3A8BD9",
  "#FF5733",
  "#DAF7A6",
];

export const TrendingSkills = () => {
  const matches = useMediaQuery("(max-width: 1100px)");
  const { report, reportId } = useReportStore();
  const content = report.skills_trending || [];

  const skills = content.data.map((skill, index) => ({
    name: skill.title,
    color: COLORS[index % COLORS.length],
    description: skill.description,
    trending: skill.trending,
  }));

  const allTrendingValues = skills.flatMap((skill) =>
    Object.values(skill.trending)
  );

  const minValue = Math.min(...allTrendingValues);
  const maxValue = Math.max(...allTrendingValues);

  if (content.mark_down === null) return "";

  return (
    <Paper radius="24px" p="32px" mt="32px" withBorder>
      <Title size={matches ? 24 : 36} mb={8}>
        {content?.mark_down ? content?.mark_down.replace("##", "") : " "}
      </Title>
      <Text style={{ fontSize: matches ? 16 : 18 }} mb={24} c="dimmed">
        {content?.explanation ? content?.explanation : " "}
      </Text>

      <ResponsiveContainer
        width={matches ? "110%" : "100%"}
        style={{ marginLeft: matches ? -10 : 0 }}
        height={matches ? 488 : 1000}
      >
        <AreaChart
          data={Object.keys(skills[0]?.trending || {}).map((year) => ({
            year,
            ...skills.reduce(
              (acc, skill) => ({ ...acc, [skill.name]: skill.trending[year] }),
              {}
            ),
          }))}
        >
          <defs>
            {skills.map((skill, index) => (
              <linearGradient
                key={index}
                id={`color${index + 1}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={skill.color} stopOpacity={0.8} />
                <stop
                  offset="95%"
                  stopColor={`${skill.color}33`}
                  stopOpacity={0}
                />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="year" />

          <YAxis
            domain={[minValue, maxValue]}
            ticks={[
              minValue,
              maxValue / 4,
              maxValue / 2,
              (maxValue * 3) / 4,
              maxValue,
            ]}
          />
          <Tooltip />
          {skills.map((skill, index) => (
            <Area
              key={index}
              type="linear"
              dataKey={skill.name}
              stroke={skill.color}
              strokeWidth={3}
              fill={`url(#color${index + 1})`}
              fillOpacity={1}
            />
          ))}
          {skills.map((skill, index) => (
            <Line
              key={index}
              type="linear"
              dataKey={skill.name}
              stroke={skill.color}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      {skills.map((skill, index) => (
        <Flex gap={5} mt={16} key={index}>
          <ColorSwatch color={skill.color} size={14} radius={2} mt={5} />
          <Text size={matches ? "m" : "lg"}>
            <b>{skill.name}</b>: {skill.description}
          </Text>
        </Flex>
      ))}

      <LikeResponse report_id={reportId} section="skill_trending" />
    </Paper>
  );
};
